import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  useTheme,
  alpha,
  Grid,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Button,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Fade,
  Grow,
  Alert,
  Link
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PaymentIcon from "@mui/icons-material/Payment";
import PeopleIcon from "@mui/icons-material/People";
import HomeIcon from "@mui/icons-material/Home";
import WarningIcon from "@mui/icons-material/Warning";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SupportLayout from "./SupportLayout";

const FAQCategory = ({ title, icon, color, faqs, searchQuery, defaultExpanded = false }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [feedback, setFeedback] = useState({});

  const handleFeedback = (faqId, helpful) => {
    setFeedback(prev => ({ ...prev, [faqId]: helpful }));
    console.log(`FAQ ${faqId} was ${helpful ? 'helpful' : 'not helpful'}`);
  };

  const copyLink = (question) => {
    const url = `${window.location.origin}${window.location.pathname}?search=${encodeURIComponent(question)}`;
    navigator.clipboard.writeText(url);
  };

  // Filter FAQs based on search
  const filteredFaqs = searchQuery 
    ? faqs.filter(faq => 
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqs;

  if (filteredFaqs.length === 0) return null;

  return (
    <Card sx={{ mb: 4, borderRadius: 3 }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          boxShadow: "none",
          "&:before": { display: "none" },
          "&.Mui-expanded": { margin: 0 }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            p: 3,
            "& .MuiAccordionSummary-content": { alignItems: "center" }
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: alpha(color, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
              color: color
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {filteredFaqs.length} questions
            </Typography>
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ p: 3, pt: 0 }}>
            {filteredFaqs.map((faq, index) => (
              <Grow in timeout={index * 100} key={faq.id}>
                <Box>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 2,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        boxShadow: theme.shadows[2]
                      }
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      {faq.question}
                    </Typography>
                    
                    <Typography variant="body1" sx={{ color: "text.secondary", mb: 2, lineHeight: 1.6 }}>
                      {faq.answer}
                    </Typography>
                    
                    {faq.proTip && (
                      <Box sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                          💡 Pro Tip
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {faq.proTip}
                        </Typography>
                      </Box>
                    )}
                    
                    {faq.videoUrl && (
                      <Button
                        size="small"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => window.open(faq.videoUrl, '_blank')}
                        sx={{ mb: 2 }}
                      >
                        Watch video tutorial
                      </Button>
                    )}
                    
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          Was this helpful?
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleFeedback(faq.id, true)}
                          sx={{ color: feedback[faq.id] === true ? theme.palette.success.main : "inherit" }}
                        >
                          <ThumbUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleFeedback(faq.id, false)}
                          sx={{ color: feedback[faq.id] === false ? theme.palette.error.main : "inherit" }}
                        >
                          <ThumbDownIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <IconButton size="small" onClick={() => copyLink(faq.question)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                </Box>
              </Grow>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

const FAQs = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const tabsRef = useRef(null);
  const contentRef = useRef(null);

  // Map category names to tab indices
  const categoryToTabIndex = {
    'all': 0,
    'scheduling': 1,
    'payment': 2,
    'cleaners': 3,
    'properties': 4,
    'emergency': 5
  };

  // Map tab index to category name
  const tabIndexToCategory = {
    0: 'all',
    1: 'scheduling',
    2: 'payment',
    3: 'cleaners',
    4: 'properties',
    5: 'emergency'
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search');
    const category = params.get('category');
    
    if (search) {
      setSearchQuery(search);
    }
    
    // Handle category parameter
    if (category && categoryToTabIndex[category.toLowerCase()] !== undefined) {
      const tabIndex = categoryToTabIndex[category.toLowerCase()];
      setActiveTab(tabIndex);
      
      // Don't scroll to top when category changes via URL
      // Only scroll if it's an initial load with a specific category
      const isInitialLoad = !sessionStorage.getItem('faqs_initialized');
      if (!isInitialLoad) {
        // Save scroll position before category change
        const scrollPosition = window.scrollY;
        sessionStorage.setItem('faqs_scroll_position', scrollPosition);
      }
      sessionStorage.setItem('faqs_initialized', 'true');
    }
  }, [location.search]);

  // Restore scroll position when component mounts
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('faqs_scroll_position');
    if (savedScrollPosition && parseInt(savedScrollPosition) > 0) {
      window.scrollTo({ top: parseInt(savedScrollPosition), behavior: 'instant' });
      sessionStorage.removeItem('faqs_scroll_position');
    }
    sessionStorage.removeItem('faqs_initialized');
  }, []);

  const faqsData = {
    scheduling: [
      {
        id: "sched1",
        question: "How do I create a cleaning schedule?",
        answer: "Navigate to your Dashboard → Properties → Select a property → 'Create Schedule'. You'll need to set the date, time, frequency (one-time or recurring), and any special instructions. Once reviewed, click 'Publish' to make the schedule available for cleaners.",
        proTip: "Use recurring schedules for regular cleanings to save time. You can set weekly, bi-weekly, or monthly frequencies.",
        videoUrl: "https://www.youtube.com/watch?v=example1"
      },
      {
        id: "sched2",
        question: "Can I modify or cancel a booking after it's been accepted?",
        answer: "Yes. Go to 'Upcoming Schedules' → Select the booking → Click 'Modify' or 'Cancel'. Changes made within 24 hours of the scheduled time may incur a cancellation fee. Please review our cancellation policy for details.",
        proTip: "If you need to cancel, do it as early as possible to avoid fees and give cleaners time to adjust their schedules."
      },
      {
        id: "sched3",
        question: "How do recurring cleanings work?",
        answer: "Recurring cleanings automatically generate new schedules based on your chosen frequency (weekly, bi-weekly, monthly, or custom). You can set an end date or let them continue indefinitely. You'll receive a notification before each recurring cleaning is scheduled."
      },
      {
        id: "sched4",
        question: "What happens if a cleaner cancels last minute?",
        answer: "We have an automatic backup system that immediately notifies other available cleaners in your area. If no replacement is found within 2 hours, you'll receive a 100% refund plus a 20% discount on your next booking. You can also manually select a replacement from your preferred cleaners list."
      }
    ],
    payment: [
      {
        id: "pay1",
        question: "How does the payment system work?",
        answer: "Payments are processed securely through Stripe. You'll be charged after the cleaning is completed and approved. You can review the invoice, add tips, and rate the cleaner before payment is finalized. All major credit cards, debit cards, Apple Pay, and Google Pay are accepted."
      },
      {
        id: "pay2",
        question: "What's your refund policy?",
        answer: "If you're not satisfied with the cleaning, you have 24 hours to report issues. We'll either send the cleaner back for a re-clean or issue a partial/full refund based on the situation. Cancellations made more than 24 hours in advance receive a full refund."
      },
      {
        id: "pay3",
        question: "Can I see an invoice before being charged?",
        answer: "Yes. After each cleaning, you'll receive a detailed invoice showing the breakdown of costs including labor, supplies, and any additional services. You have 2 hours to review and dispute before automatic payment."
      },
      {
        id: "pay4",
        question: "How do I update my payment method?",
        answer: "Go to Settings → Payment Methods → Add or remove cards. You can set a default payment method or choose different ones for different properties. We use Stripe's secure tokenization, so we never store your full card details."
      }
    ],
    cleaners: [
      {
        id: "clean1",
        question: "Can I choose a specific cleaner for my property?",
        answer: "Absolutely! When creating a schedule, you'll see a 'Choose Cleaner' option. You can browse cleaner profiles, view their ratings, reviews, and completion rates. Once you find one you like, you can assign them to future bookings or add them to your preferred cleaners list."
      },
      {
        id: "clean2",
        question: "How do I review a cleaner after service?",
        answer: "After a cleaning is completed, you'll receive a push notification and email asking for a review. You can rate 1-5 stars in categories like punctuality, thoroughness, and professionalism. Written comments are anonymous to protect your privacy."
      },
      {
        id: "clean3",
        question: "Can I request the same cleaner every time?",
        answer: "Yes! Cleaners you've worked with before will appear in your 'Preferred Cleaners' list. When creating recurring schedules, you can assign your preferred cleaner. We'll always try to match you with them first, but if they're unavailable, we'll find the next best match."
      },
      {
        id: "clean4",
        question: "What if my cleaner doesn't show up?",
        answer: "If a cleaner no-shows, we immediately dispatch a backup cleaner or reschedule at no additional cost. You'll receive a 50% discount on your next service as an apology. Contact our 24/7 emergency support if this happens."
      }
    ],
    properties: [
      {
        id: "prop1",
        question: "How do I add special instructions for cleaners?",
        answer: "You can add property-specific instructions in Property Details → Access Instructions. You can also add job-specific instructions when creating each schedule. Instructions can include parking info, door codes, pet information, fragile items, and preferred cleaning products."
      },
      {
        id: "prop2",
        question: "What's included in a standard cleaning?",
        answer: "Standard cleaning includes: dusting all surfaces, vacuuming and mopping floors, cleaning bathrooms (sinks, toilets, showers, mirrors), kitchen cleaning (counters, appliances exterior, sink), trash removal, and making beds. Deep cleaning and move-in/out cleaning include additional services."
      },
      {
        id: "prop3",
        question: "How do I update my property details?",
        answer: "Go to Properties → Select your property → 'Edit Property'. You can update photos, room count, square footage, amenities, access instructions, and parking information. Changes are saved immediately and visible to cleaners."
      },
      {
        id: "prop4",
        question: "Can I add multiple properties?",
        answer: "Yes! You can add unlimited properties to your account. Each property has its own profile, settings, and preferred cleaners. You can even set different cleaning standards and instructions for each property."
      }
    ],
    emergency: [
      {
        id: "emerg1",
        question: "How do I report damage or issues after cleaning?",
        answer: "Within 24 hours of cleaning completion, go to Schedule Details → 'Report Issue'. Upload photos and describe the issue. Our claims team investigates within 48 hours. For severe damage, call our emergency support immediately.",
        proTip: "Take before and after photos of valuable items to document condition."
      },
      {
        id: "emerg2",
        question: "What if I need to cancel for an emergency?",
        answer: "Emergency cancellations (illness, property damage, natural disasters) are handled case-by-case. Call our support line immediately for assistance. With documentation, we may waive cancellation fees.",
        videoUrl: "https://www.youtube.com/watch?v=emergency"
      },
      {
        id: "emerg3",
        question: "What if a cleaner gets injured at my property?",
        answer: "All our cleaners are fully insured and covered by workers' compensation. If an accident occurs, ensure the cleaner gets immediate medical attention and contact our emergency support. Your liability is limited by our insurance agreement."
      }
    ]
  };

  const allFAQs = [
    ...faqsData.scheduling.map(f => ({ ...f, category: "scheduling", categoryTitle: "Scheduling" })),
    ...faqsData.payment.map(f => ({ ...f, category: "payment", categoryTitle: "Payment & Billing" })),
    ...faqsData.cleaners.map(f => ({ ...f, category: "cleaners", categoryTitle: "Cleaners" })),
    ...faqsData.properties.map(f => ({ ...f, category: "properties", categoryTitle: "Properties" })),
    ...faqsData.emergency.map(f => ({ ...f, category: "emergency", categoryTitle: "Emergency" }))
  ];

  const filteredFAQs = searchQuery 
    ? allFAQs.filter(faq => 
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const handleSearch = (e) => {
    e.preventDefault();
    // Update URL with search query
    const params = new URLSearchParams(location.search);
    if (searchQuery) {
      params.set('search', searchQuery);
      params.delete('category');
    } else {
      params.delete('search');
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleTabChange = (event, newValue) => {
    // Save current scroll position before changing tab
    const currentScrollPosition = window.scrollY;
    sessionStorage.setItem('faqs_scroll_position', currentScrollPosition);
    
    setActiveTab(newValue);
    
    // Update URL with category parameter
    const category = tabIndexToCategory[newValue];
    const params = new URLSearchParams(location.search);
    
    if (category && category !== 'all') {
      params.set('category', category);
      params.delete('search');
      setSearchQuery('');
    } else {
      params.delete('category');
    }
    
    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`, { replace: true });
    
    // Restore scroll position after a brief delay to allow DOM to update
    setTimeout(() => {
      const savedPosition = sessionStorage.getItem('faqs_scroll_position');
      if (savedPosition) {
        window.scrollTo({ top: parseInt(savedPosition), behavior: 'instant' });
        sessionStorage.removeItem('faqs_scroll_position');
      }
    }, 50);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setActiveTab(0);
    navigate(location.pathname, { replace: true });
  };

  const categories = [
    { label: "All Questions", value: "all", icon: <HelpOutlineIcon /> },
    { label: "Scheduling", value: "scheduling", icon: <ScheduleIcon /> },
    { label: "Payment", value: "payment", icon: <PaymentIcon /> },
    { label: "Cleaners", value: "cleaners", icon: <PeopleIcon /> },
    { label: "Properties", value: "properties", icon: <HomeIcon /> },
    { label: "Emergency", value: "emergency", icon: <WarningIcon /> }
  ];

  return (
    <SupportLayout
      title="Frequently Asked Questions"
      category="Host FAQ"
      subtitle="Everything you need to know about hosting with FreshSweeper"
      breadcrumbs={[
        { label: "Help Center", link: "/help" },
        { label: "FAQs" }
      ]}
    >
      {/* Search Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <form onSubmit={handleSearch}>
          <TextField
            fullWidth
            placeholder="Search for answers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary, ml: 1 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <Button onClick={() => {
                    setSearchQuery('');
                    const params = new URLSearchParams(location.search);
                    params.delete('search');
                    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`, { replace: true });
                  }} size="small">
                    Clear
                  </Button>
                </InputAdornment>
              ),
              sx: { py: 1.5, px: 1 }
            }}
          />
        </form>
      </Paper>

      {/* Active Filters Indicator */}
      {(searchQuery || activeTab !== 0) && (
        <Alert 
          severity="info" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={clearFilters}>
              Clear all filters
            </Button>
          }
        >
          {searchQuery && `Showing results for: "${searchQuery}"`}
          {searchQuery && activeTab !== 0 && ' • '}
          {activeTab !== 0 && `Category: ${categories[activeTab].label}`}
        </Alert>
      )}

      {/* Category Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ mb: 4, borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
        scrollButtons="auto"
        ref={tabsRef}
      >
        {categories.map((cat, index) => (
          <Tab 
            key={cat.value} 
            label={cat.label} 
            icon={cat.icon}
            iconPosition="start"
          />
        ))}
      </Tabs>

      {/* FAQ Content */}
      <div ref={contentRef}>
        {!searchQuery ? (
          <>
            {activeTab === 0 && (
              <>
                <FAQCategory
                  title="Scheduling & Bookings"
                  icon={<ScheduleIcon />}
                  color={theme.palette.primary.main}
                  faqs={faqsData.scheduling}
                  searchQuery={searchQuery}
                  defaultExpanded={false}
                />
                <FAQCategory
                  title="Payment & Billing"
                  icon={<PaymentIcon />}
                  color="#10b981"
                  faqs={faqsData.payment}
                  searchQuery={searchQuery}
                  defaultExpanded={false}
                />
                <FAQCategory
                  title="Managing Cleaners"
                  icon={<PeopleIcon />}
                  color="#f59e0b"
                  faqs={faqsData.cleaners}
                  searchQuery={searchQuery}
                  defaultExpanded={false}
                />
                <FAQCategory
                  title="Property Management"
                  icon={<HomeIcon />}
                  color="#8b5cf6"
                  faqs={faqsData.properties}
                  searchQuery={searchQuery}
                  defaultExpanded={false}
                />
                <FAQCategory
                  title="Emergency & Issues"
                  icon={<WarningIcon />}
                  color="#ef4444"
                  faqs={faqsData.emergency}
                  searchQuery={searchQuery}
                  defaultExpanded={false}
                />
              </>
            )}
            {(activeTab === 1 || activeTab === 0) && activeTab !== 0 && (
              <FAQCategory
                title="Scheduling & Bookings"
                icon={<ScheduleIcon />}
                color={theme.palette.primary.main}
                faqs={faqsData.scheduling}
                searchQuery={searchQuery}
                defaultExpanded={false}
              />
            )}
            {(activeTab === 2 || activeTab === 0) && activeTab !== 0 && (
              <FAQCategory
                title="Payment & Billing"
                icon={<PaymentIcon />}
                color="#10b981"
                faqs={faqsData.payment}
                searchQuery={searchQuery}
                defaultExpanded={false}
              />
            )}
            {(activeTab === 3 || activeTab === 0) && activeTab !== 0 && (
              <FAQCategory
                title="Managing Cleaners"
                icon={<PeopleIcon />}
                color="#f59e0b"
                faqs={faqsData.cleaners}
                searchQuery={searchQuery}
                defaultExpanded={false}
              />
            )}
            {(activeTab === 4 || activeTab === 0) && activeTab !== 0 && (
              <FAQCategory
                title="Property Management"
                icon={<HomeIcon />}
                color="#8b5cf6"
                faqs={faqsData.properties}
                searchQuery={searchQuery}
                defaultExpanded={false}
              />
            )}
            {(activeTab === 5 || activeTab === 0) && activeTab !== 0 && (
              <FAQCategory
                title="Emergency & Issues"
                icon={<WarningIcon />}
                color="#ef4444"
                faqs={faqsData.emergency}
                searchQuery={searchQuery}
                defaultExpanded={false}
              />
            )}
          </>
        ) : (
          <Box>
            {filteredFAQs.length > 0 ? (
              filteredFAQs.map((faq, index) => (
                <Grow in timeout={index * 100} key={index}>
                  <Paper
                    sx={{
                      p: 3,
                      mb: 2,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}
                  >
                    <Chip
                      label={faq.categoryTitle}
                      size="small"
                      sx={{ mb: 1, background: alpha(theme.palette.primary.main, 0.1) }}
                    />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {faq.question}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                      {faq.answer}
                    </Typography>
                  </Paper>
                </Grow>
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <HelpOutlineIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                <Typography variant="h6">No results found</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                  Try different keywords or browse the categories above
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 3 }}
                  onClick={clearFilters}
                >
                  Clear Search
                </Button>
              </Box>
            )}
          </Box>
        )}
      </div>

      {/* Still Need Help */}
      <Paper
        sx={{
          mt: 6,
          p: 4,
          borderRadius: 3,
          textAlign: "center",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
        }}
      >
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          Still have questions?
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
          Our support team is here to help you 24/7
        </Typography>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
          <Button variant="contained" component={Link} to="/contact">
            Contact Support
          </Button>
          <Button variant="outlined" component={Link} to="/help">
            Browse Help Center
          </Button>
        </Box>
      </Paper>
    </SupportLayout>
  );
};

export default FAQs;